/**
 * The five Host Panel tabs of the v2 design (`Painel do Anfitriao v2`, handoff §3), with copy
 * taken literally from it.
 *
 * Deliberate divergence from handoff §3, which proposes `/minha-lista/*`: the product
 * already routes `/anfitriao/*`, wired through `next.config.ts` and `robots.ts` and fixed
 * by the `route-naming` steering.
 */

import type { ListLifecycle } from "./host-panel-types";

export type HostTabKey = "painel" | "lista" | "evento" | "extrato" | "dados";

export type HostTab = {
  key: HostTabKey;
  /** Label in the header. */
  label: string;
  /** Short label, used as the menu eyebrow. */
  eyebrow: string;
  /** One line on what the tab does, shown in the menu. */
  description: string;
  href: string;
};

export const HOST_TABS: readonly HostTab[] = [
  {
    key: "painel",
    label: "Meu Painel",
    eyebrow: "Painel",
    description: "Crédito, presentes e curadoria da sua lista.",
    href: "/anfitriao/dashboard",
  },
  {
    key: "lista",
    label: "Minha Lista",
    eyebrow: "Lista",
    description: "A capa e o recado que os convidados veem.",
    href: "/anfitriao/lista",
  },
  {
    key: "evento",
    label: "Dados do Evento",
    eyebrow: "Evento",
    description: "O casal, a data, a assessoria e as boas-vindas.",
    href: "/anfitriao/evento",
  },
  {
    key: "extrato",
    label: "Presentes & Créditos",
    eyebrow: "Créditos",
    description: "Presentes recebidos e resgates do seu saldo.",
    href: "/anfitriao/creditos",
  },
  {
    key: "dados",
    label: "Minha Conta",
    eyebrow: "Conta",
    description: "Seus dados, endereços e pedidos em todo canal.",
    href: "/anfitriao/conta",
  },
];

/**
 * Which tabs a host sees — the board's ruling of 2026-09-29.
 *
 * - **Meu Painel** once the list has been published: before that there is no credit, no gift and
 *   no visitor to read, only a draft, and the draft's place is Minha Lista. Paused and closed
 *   lists were published, so they keep it.
 * - **Presentes & Créditos** once the first gift has arrived: a statement with nothing in it is
 *   a tab that answers no question yet.
 *
 * Minha Lista, Dados do Evento and Minha Conta are always there.
 */
export type HostTabAccess = {
  dashboard: boolean;
  credits: boolean;
};

export function hostTabAccess(panel: {
  lifecycle: ListLifecycle;
  summary: { piecesCount: number };
  recentGifts: readonly unknown[];
}): HostTabAccess {
  return {
    dashboard: panel.lifecycle !== "rascunho",
    credits: panel.summary.piecesCount > 0 || panel.recentGifts.length > 0,
  };
}

export function visibleHostTabs(access: HostTabAccess): readonly HostTab[] {
  return HOST_TABS.filter(
    (tab) => (tab.key !== "painel" || access.dashboard) && (tab.key !== "extrato" || access.credits),
  );
}

/** A tab's address, by key: positions change when a tab is added, keys do not. */
export function hostTabHref(key: HostTabKey): string {
  const tab = HOST_TABS.find((candidate) => candidate.key === key);
  if (tab === undefined) throw new Error(`Unknown host tab: ${key}`);

  return tab.href;
}

/**
 * **The host's own place** — the one definition of it.
 *
 * Meu Painel once the list is on the air; Minha Lista before that, because a draft has no Meu
 * Painel tab at all (`hostTabAccess`). It answers two questions with the same reading: where a
 * host lands when the tab they asked for is not theirs yet, and where the header's wordmark
 * takes them (`logoDestination`).
 */
export function hostLandingTab(access: HostTabAccess): HostTab {
  const key: HostTabKey = access.dashboard ? "painel" : "lista";
  const tab = HOST_TABS.find((candidate) => candidate.key === key);
  if (tab === undefined) throw new Error(`Unknown host tab: ${key}`);

  return tab;
}

/** Where a host lands when the tab they asked for is not theirs yet. */
export function hostLandingHref(access: HostTabAccess): string {
  return hostLandingTab(access).href;
}

/**
 * Which tab a path belongs to, matched by prefix so child routes keep the tab marked.
 */
export function findActiveTab(pathname: string): HostTab | undefined {
  return HOST_TABS.find((tab) => pathname === tab.href || pathname.startsWith(`${tab.href}/`));
}

/**
 * Where a request for `address` (path and query, as the proxy hands it to the render) has to go
 * because the tab it names is not this host's yet — or `null` when it can stay.
 *
 * The same rule the tabs' own pages apply (`dashboard/page.tsx`, `creditos/page.tsx`), asked
 * **before the shell is sent**. A page redirects only after the layout has been streamed (each
 * tab's `loading.tsx` commits a 200 first), which turned the redirect into a meta refresh: the
 * shell hydrated, counted a page view for a tab nobody saw, and only then moved on (TAG-12). The
 * pages keep their `redirect()` for client navigations, where the layout is not rendered again.
 */
export function resolveTabRedirect(address: string | null, access: HostTabAccess): string | null {
  if (address === null) return null;

  const pathname = address.split(/[?#]/, 1)[0].replace(/\/+$/, "");
  const tab = findActiveTab(pathname);
  if (tab === undefined) return null;

  const isDenied = (tab.key === "painel" && !access.dashboard) || (tab.key === "extrato" && !access.credits);

  return isDenied ? hostLandingHref(access) : null;
}

/**
 * Avatar initials, as the prototype writes them: the first two names of the person, upper-cased.
 * "Maria Helena" + "Almeida" is "MH" — the name the panel greets — and a single first name
 * reaches into the family name ("Ana" + "Moretti" is "AM").
 */
export function initialsFor(firstName: string, lastName: string): string {
  const names = `${firstName} ${lastName}`.trim().split(/\s+/).filter(Boolean);

  return names
    .slice(0, 2)
    .map((name) => name.charAt(0))
    .join("")
    .toUpperCase();
}
